import React from 'react';
import { Download, FileText, Loader2, Lock } from 'lucide-react';
import { PLAN_PRICES_USD, formatUsd } from '../../../data/pricingPlans';
import type { RiskLevel } from '../../../utils/scanReport';
interface PdfReportPreviewProps {
  score: number;
  riskLevel: RiskLevel;
  patternCount: number;
  isUnlocked: boolean;
  isGenerating: boolean;
  onUnlock: () => void;
  onDownload: () => void;
}
const RISK_TEXT: Record<RiskLevel, string> = {
  high: 'text-forensic-crimson-soft',
  medium: 'text-amber-300',
  low: 'text-emerald-300'
};
export function PdfReportPreview({
  score,
  riskLevel,
  patternCount,
  isUnlocked,
  isGenerating,
  onUnlock,
  onDownload
}: PdfReportPreviewProps) {
  return <section aria-labelledby="pdf-heading" className="grid gap-8 rounded-2xl border border-forensic-line bg-forensic-surface p-6 sm:grid-cols-[168px_1fr] sm:items-center sm:p-8" data-id="element-272" data-mp="21qz5o84cp7e">
      {/* Miniature report */}
      <div className="relative mx-auto w-[168px]" data-id="element-273" data-mp="1dgmc95r4ailu">
        <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-forensic-line-strong bg-forensic-card p-3" data-id="element-274" data-mp="e9gti55ff3t0">
          <p className="font-mono text-[7px] font-bold uppercase tracking-[0.18em] text-forensic-muted" data-id="element-275" data-mp="j78mz3lpyxzb">
            ToxicDetector.ai
          </p>
          <p className="font-mono text-[7px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-276" data-mp="n829ptnfe6qm">
            Forensic Report
          </p>
          <div className="mt-5 text-center" data-id="element-277" data-mp="3rowd40lvgf55">
            <p className="text-4xl font-semibold leading-none text-white tabular-nums" data-id="element-278" data-mp="3rpxx4ukv8bz0">{score}</p>
            <p className={`mt-1.5 text-[8px] font-semibold uppercase tracking-widest ${RISK_TEXT[riskLevel]}`} data-id="element-279" data-mp="3vpyh0uqqhfdg">
              {riskLevel} risk
            </p>
          </div>
          <p className="mt-4 text-center text-[8px] text-forensic-muted" data-id="element-280" data-mp="9avug91wuq65">
            {patternCount} {patternCount === 1 ? 'pattern' : 'patterns'} detected
          </p>
          <div className="mt-3 space-y-1" aria-hidden="true" data-id="element-281" data-mp="3cyp225y71yeh">
            <div className="h-1 w-full rounded-full bg-white/10" data-id="element-282" data-mp="3cx4bssiwx1mf" />
            <div className="h-1 w-4/5 rounded-full bg-white/10" data-id="element-283" data-mp="k3feirgw84bs" />
            <div className="h-1 w-3/5 rounded-full bg-white/10" data-id="element-284" data-mp="f65igsz00uez" />
          </div>
        </div>
        {!isUnlocked && <div className="absolute inset-x-px bottom-px flex h-1/3 items-center justify-center gap-1.5 rounded-b-lg border-t border-forensic-line bg-forensic-card/95 text-[8px] font-medium text-forensic-muted" data-id="element-285" data-mp="svz83dibvipr">
            <Lock size={9} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-286" data-mp="16nsia9yvb41" />
            Detailed pages locked
          </div>}
      </div>

      <div data-id="element-287" data-mp="a6auoyaisky5">
        <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-forensic-subtle" data-id="element-288" data-mp="a6sxkdrotxf1">
          <FileText size={12} aria-hidden="true" data-id="element-289" data-mp="56ob8743m8gw" />
          PDF Report
        </p>
        <h2 id="pdf-heading" className="mt-2 text-lg font-semibold text-white sm:text-xl" data-id="element-290" data-mp="1lkocbleok45q">
          {isUnlocked ? 'Your complete report is ready' : 'Complete report locked'}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-forensic-muted" data-id="element-291" data-mp="1p3dokb2m4z8n">
          {isUnlocked ? 'Executive summary, every finding with its evidence, the message breakdown, and recommendations — in one document.' : `The cover is free. Detailed findings, evidence pages, and recommendations unlock with your full forensic report for ${formatUsd(PLAN_PRICES_USD.single_scan)}.`}
        </p>
        <div className="mt-5" data-id="element-292" data-mp="27v69di7j3e5a">
          {isUnlocked ? <button type="button" onClick={onDownload} disabled={isGenerating} className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-forensic-line-strong px-5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/30 hover:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft disabled:cursor-wait disabled:opacity-70" data-id="element-293" data-mp="2ct7xihjta3nm">
              {isGenerating ? <Loader2 size={16} className="animate-spin" aria-hidden="true" data-id="element-294" data-mp="16yxtoclvpb7p" /> : <Download size={16} aria-hidden="true" data-id="element-295" data-mp="12v86nrkur1os" />}
              {isGenerating ? 'Preparing PDF…' : 'Download PDF report'}
            </button> : <button type="button" onClick={onUnlock} className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:text-forensic-crimson-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-forensic-crimson-soft" data-id="element-296" data-mp="syn1954x7okf">
              <Lock size={14} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-297" data-mp="1gu7yt97kpypp" />
              Unlock your full forensic report
            </button>}
        </div>
      </div>
    </section>;
}